/**
 * Derive a human-readable display name from an email address.
 *
 * Per contracts/launcher-home.yaml: there is no `display_name` column in
 * allowed_users and Google OAuth given_name isn't cached anywhere. The
 * email local-part (everything before `@`), split on `.`, `_`, or `-`,
 * stripped of trailing digits, and title-cased is the sole source of truth.
 *
 *   alice.smith@example.com   -> "Alice Smith"
 *   bob@example.com           -> "Bob"
 *   foo_bar.baz@x.com         -> "Foo Bar Baz"
 *   jean-luc.picard@…         -> "Jean Luc Picard"
 *   yashrajgandhi95@gmail.com -> ""   (too long, no separators)
 *   yr95@gmail.com            -> "Yr"
 *
 * When the local-part is one long run-on word with no separators and
 * more than 11 characters, return empty string so the greeting falls
 * through to an unnamed form ("Good afternoon.") rather than rendering
 * an ugly concatenation like "Yashrajgandhi".
 */

const MAX_RUNON_LENGTH = 11;

export function displayNameFromEmail(email: string): string {
  if (!email) return '';
  const local = email.split('@')[0] ?? '';
  if (!local) return '';

  const rawSegments = local.split(/[._-]+/).filter(Boolean);
  // Strip trailing digits from each segment: "yash95" -> "yash".
  const segments = rawSegments
    .map((s) => s.replace(/\d+$/, ''))
    .filter(Boolean);

  if (segments.length === 0) return '';

  // If there's only one segment AND it's a long run-on (>14 chars) with
  // no separators in the original, bail — "Yashrajgandhi" is ugly.
  if (segments.length === 1 && segments[0].length > MAX_RUNON_LENGTH) {
    return '';
  }

  return segments
    .map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}
